import { LiveAnnouncer } from '@angular/cdk/a11y';
import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatMenuItem } from '@angular/material/menu';
import { CmLocalizationService, CmSettingsService } from '@ats/cm/core';
import { Unsubscribable } from '@ats/utility';
import { combineLatest, Observable } from 'rxjs';
import { debounceTime, map, switchMap, takeUntil } from 'rxjs/operators';
import { CmTableService } from '../../services/table.service';

@Component({
    selector: 'cm-table-search',
    templateUrl: './table-search.component.html',
    styleUrls: ['./table-search.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CmTableSearchComponent extends Unsubscribable implements OnInit {
	form: UntypedFormGroup;
	columns$: Observable<{ field: string; label: string; selected: boolean; localizationKey?: string }[]>;
	isFiltered$: Observable<boolean>;
	filterButtonTooltip$: Observable<string>;
	toggleColumnSearch: (field?: string) => void;
	@ViewChild('firstMenuItem') firstMenuItem: MatMenuItem;

	constructor(
		private readonly waiter: CmTableService,
		private readonly fb: UntypedFormBuilder,
		private readonly announcer: LiveAnnouncer,
		private readonly settingsService: CmSettingsService,
		private readonly localizeService: CmLocalizationService
	) {
		super();
	}

	ngOnInit() {
		this.columns$ = this.waiter.searchedColumns$;
		this.isFiltered$ = this.columns$.pipe(map(columns => !!columns.filter(col => col.selected).length));

		this.toggleColumnSearch = this.waiter.toggleColumnSearch;

		this.form = this.fb.group({
			search: '',
			searchedColumns: [],
		});

		this.search.valueChanges.pipe(takeUntil(this.unsub), debounceTime(350)).subscribe(searchVal => {
			this.waiter.updateSearch(searchVal);
			this.announcer.announce(
				`${this.localizeService.localize(
					`${this.settingsService.portalCoreFeature}/System.Global.Notification.SearchResultsAnnouncement`,
					'Search results displayed:'
				)} ${(this.waiter.tableDatasource.filteredData || []).length}`
			);
		});

		this.initiateFilterSubscription();
	}

	// There is a bug with Material where the first item does not get focus when opened while a screen reader is being used.
	// https://github.com/angular/components/issues/1673
	// This function fixes that.
	focusFirstItem(): void {
		this.firstMenuItem.focus('keyboard');
	}

	get search(): AbstractControl {
		return this.form.get('search') as AbstractControl;
	}

	private initiateFilterSubscription(): void {
		const searchedColumns$ = this.waiter.searchedColumns$.pipe(
			switchMap(searchedColumns => {
				const selectedColumn = searchedColumns.find(col => col.selected);
				return selectedColumn
					? this.localizeService.localize$(selectedColumn.localizationKey, selectedColumn.label)
					: this.localizeService.localize$('Core/System.Table.MenuItem.ClearSearchColumn', 'All Columns');
			})
		);
		const defaultText$ = this.localizeService.localize$(
			'Core/System.Table.Button.ColumnSearch',
			'Select Columns to Search'
		);

		this.filterButtonTooltip$ = combineLatest([defaultText$, searchedColumns$]).pipe(
			map(([text, columns]) => text + ' - ' + columns)
		);
	}
}
